import { Component, ReactNode } from "react"

import * as Sentry from "@sentry/react"

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

class SentryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentDidCatch(error: Error, errorInfo: any) {
    // Only send error to Sentry if it's initialized (production)
    if (import.meta.env.PROD) {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      })
    } else {
      // In development, just log to console
      console.error("Error caught by ErrorBoundary:", error, errorInfo)
    }
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        this.props.fallback || (
          <div className="min-h-screen flex items-center justify-center bg-palette-jungleGreen100">
            <div className="max-w-md mx-auto text-center">
              <h1 className="text-2xl font-bold text-palette-jungleGreen900 mb-4">
                Something went wrong
              </h1>
              <p className="text-palette-jungleGreen600 mb-6">
                {import.meta.env.PROD
                  ? "An error has occurred and been reported to our team."
                  : "An error occurred. Check the console for details."}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
              >
                Reload Page
              </button>
              {process.env.NODE_ENV === "development" && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-sm text-palette-jungleGreen500">
                    Error Details (Development Only)
                  </summary>
                  <pre className="mt-2 text-xs text-red-600 whitespace-pre-wrap">
                    {this.state.error?.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )
      )
    }

    return this.props.children
  }
}

export default SentryErrorBoundary
